<template>
  <button
    type="button"
    class="qp-theme-toggle"
    :class="{ 'qp-theme-toggle--dark': isDark }"
    :aria-label="isDark ? $t('theme.switch_to_light') : $t('theme.switch_to_dark')"
    :aria-pressed="isDark"
    @click="handleToggle"
  >
    <span class="qp-theme-toggle__track" aria-hidden="true">
      <svg class="qp-theme-toggle__glyph qp-theme-toggle__glyph--sun" viewBox="0 0 16 16" width="12" height="12">
        <circle cx="8" cy="8" r="3" fill="currentColor" />
        <g stroke="currentColor" stroke-width="1.2" stroke-linecap="round">
          <line x1="8" y1="1.5" x2="8" y2="3" />
          <line x1="8" y1="13" x2="8" y2="14.5" />
          <line x1="1.5" y1="8" x2="3" y2="8" />
          <line x1="13" y1="8" x2="14.5" y2="8" />
          <line x1="3.2" y1="3.2" x2="4.3" y2="4.3" />
          <line x1="11.7" y1="11.7" x2="12.8" y2="12.8" />
          <line x1="3.2" y1="12.8" x2="4.3" y2="11.7" />
          <line x1="11.7" y1="4.3" x2="12.8" y2="3.2" />
        </g>
      </svg>
      <svg class="qp-theme-toggle__glyph qp-theme-toggle__glyph--moon" viewBox="0 0 16 16" width="12" height="12">
        <path d="M13.2 10.3a5.5 5.5 0 1 1-7.5-7.5 6.3 6.3 0 0 0 7.5 7.5z" fill="currentColor" />
      </svg>
      <span class="qp-theme-toggle__knob"></span>
    </span>
  </button>
</template>

<script>
import { useTheme } from '@/composables/useTheme';

export default {
  name: 'ThemeToggle',
  setup() {
    const { isDark, toggleTheme } = useTheme();
    return { isDark, handleToggle: toggleTheme };
  },
};
</script>

<style scoped>
.qp-theme-toggle {
  display: inline-flex;
  align-items: center;
  padding: 0;
  background: transparent;
  border: 0;
  cursor: pointer;
  line-height: 0;
}

.qp-theme-toggle:focus-visible {
  outline: 0;
  border-radius: var(--radius-pill);
  box-shadow: var(--shadow-focus);
}

.qp-theme-toggle__track {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  width: 44px;
  height: 22px;
  padding-inline: 4px;
  background: var(--surface-sunken);
  border: 1px solid var(--hairline);
  border-radius: var(--radius-pill);
  transition:
    background-color var(--dur-md) var(--ease-out-quart),
    border-color var(--dur-sm) var(--ease-out-quart);
}

.qp-theme-toggle--dark .qp-theme-toggle__track {
  background: var(--accent-tint);
  border-color: var(--hairline-accent);
}

.qp-theme-toggle:hover .qp-theme-toggle__track {
  border-color: var(--accent);
}

.qp-theme-toggle:active .qp-theme-toggle__track {
  transform: scale(0.96);
  transition-duration: var(--dur-2xs);
}

.qp-theme-toggle__glyph {
  position: relative;
  z-index: 1;
  color: var(--ink-faint);
  transition: color var(--dur-sm) var(--ease-out-quart),
              opacity var(--dur-sm) var(--ease-out-quart);
}

.qp-theme-toggle__glyph--sun {
  color: var(--ink);
  opacity: 1;
}

.qp-theme-toggle__glyph--moon {
  color: var(--ink-faint);
  opacity: 0.5;
}

.qp-theme-toggle--dark .qp-theme-toggle__glyph--sun {
  color: var(--ink-faint);
  opacity: 0.5;
}

.qp-theme-toggle--dark .qp-theme-toggle__glyph--moon {
  color: var(--ink);
  opacity: 1;
}

.qp-theme-toggle__knob {
  position: absolute;
  top: 50%;
  left: 1px;
  width: 18px;
  height: 18px;
  background: var(--surface-base);
  border: 1px solid var(--hairline-strong);
  border-radius: var(--radius-pill);
  transform: translateY(-50%) rotate(0deg);
  transition: transform 420ms var(--ease-out-expo),
              background-color var(--dur-sm) var(--ease-out-quart),
              border-color var(--dur-sm) var(--ease-out-quart);
  box-shadow: var(--shadow-xs);
}

.qp-theme-toggle--dark .qp-theme-toggle__knob {
  transform: translate(22px, -50%) rotate(180deg);
  border-color: var(--accent);
}

@media (prefers-reduced-motion: reduce) {
  .qp-theme-toggle__knob,
  .qp-theme-toggle__glyph { transition: none; }
}
</style>
